<template>
  <transition name="alert">
    <div v-if="show" class="alert-overlay" @click="handleOverlayClick">
      <div class="alert-modal" :class="typeClass" @click.stop>
        <!-- Icon -->
        <div class="alert-icon">
          <i :class="iconClass"></i>
        </div>
        
        <!-- Content -->
        <div class="alert-content">
          <h3 class="alert-title">{{ title }}</h3>
          <p class="alert-message">{{ message }}</p>
        </div>
        
        <!-- Actions -->
        <div class="alert-actions">
          <button v-if="showCancel" @click="handleCancel" class="alert-btn alert-btn-cancel">
            Cancel
          </button>
          <button @click="handleConfirm" class="alert-btn alert-btn-confirm" :class="confirmBtnClass">
            {{ confirmText }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { ref, computed } from 'vue'

export default {
  name: 'CustomAlert',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'info', // info, success, warning, error, confirm
      validator: (value) => ['info', 'success', 'warning', 'error', 'confirm'].includes(value)
    },
    title: {
      type: String,
      default: ''
    },
    message: {
      type: String,
      required: true
    },
    confirmText: {
      type: String,
      default: 'OK'
    },
    showCancel: {
      type: Boolean,
      default: false
    },
    closeOnOverlay: {
      type: Boolean,
      default: true
    }
  },
  emits: ['confirm', 'cancel', 'close'],
  setup(props, { emit }) {
    const typeClass = computed(() => `alert-${props.type}`)
    
    const iconClass = computed(() => {
      const iconMap = {
        info: 'fas fa-info-circle',
        success: 'fas fa-check-circle',
        warning: 'fas fa-exclamation-triangle',
        error: 'fas fa-times-circle',
        confirm: 'fas fa-question-circle'
      }
      return iconMap[props.type] || iconMap.info
    })
    
    const confirmBtnClass = computed(() => {
      const btnMap = {
        info: 'btn-blue',
        success: 'btn-green',
        warning: 'btn-orange',
        error: 'btn-red',
        confirm: 'btn-blue'
      }
      return btnMap[props.type] || btnMap.info
    })
    
    const handleConfirm = () => {
      emit('confirm')
      emit('close')
    }
    
    const handleCancel = () => {
      emit('cancel')
      emit('close')
    }
    
    const handleOverlayClick = () => {
      if (props.closeOnOverlay) {
        emit('close')
      }
    }
    
    return {
      typeClass,
      iconClass,
      confirmBtnClass,
      handleConfirm,
      handleCancel,
      handleOverlayClick
    }
  }
}
</script>

<style lang="scss" scoped>
.alert-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
}

.alert-modal {
  background: white;
  border-radius: 16px;
  padding: 32px;
  min-width: 320px;
  max-width: 500px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  text-align: center;
  transform: scale(1);
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.alert-icon {
  margin-bottom: 16px;
  
  i {
    font-size: 48px;
  }
}

.alert-content {
  margin-bottom: 24px;
}

.alert-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: #1f2937;
}

.alert-message {
  font-size: 16px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}

.alert-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
}

.alert-btn {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 100px;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
  }
}

.alert-btn-cancel {
  background: #f3f4f6;
  color: #6b7280;
  
  &:hover {
    background: #e5e7eb;
    color: #374151;
  }
}

.alert-btn-confirm {
  color: white;
  
  &.btn-blue {
    background: #3b82f6;
    &:hover { background: #2563eb; }
  }
  
  &.btn-green {
    background: #10b981;
    &:hover { background: #059669; }
  }
  
  &.btn-orange {
    background: #f59e0b;
    &:hover { background: #d97706; }
  }
  
  &.btn-red {
    background: #ef4444;
    &:hover { background: #dc2626; }
  }
}

// Type-specific icon colors
.alert-info .alert-icon i {
  color: #3b82f6;
}

.alert-success .alert-icon i {
  color: #10b981;
}

.alert-warning .alert-icon i {
  color: #f59e0b;
}

.alert-error .alert-icon i {
  color: #ef4444;
}

.alert-confirm .alert-icon i {
  color: #6366f1;
}

// Transitions
.alert-enter-active, .alert-leave-active {
  transition: opacity 0.3s ease;
}

.alert-enter-from, .alert-leave-to {
  opacity: 0;
}

.alert-enter-active .alert-modal {
  animation: alertSlideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.alert-leave-active .alert-modal {
  animation: alertSlideOut 0.2s ease-out;
}

@keyframes alertSlideIn {
  from {
    transform: scale(0.8) translateY(-20px);
    opacity: 0;
  }
  to {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
}

@keyframes alertSlideOut {
  from {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
  to {
    transform: scale(0.9) translateY(-10px);
    opacity: 0;
  }
}

// Responsive design
@media (max-width: 480px) {
  .alert-modal {
    margin: 20px;
    padding: 24px;
    min-width: auto;
    max-width: calc(100vw - 40px);
  }
  
  .alert-actions {
    flex-direction: column;
  }
  
  .alert-btn {
    width: 100%;
  }
}
</style>